import { formatCurrency, formatDate } from '@if/core/utils'
import { AreaButton, ControlledChipsField, Help, StatCard } from '@if/mui'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import { Box, Card, CardContent, CardHeader, CircularProgress, Grid, IconButton, Stack, Typography } from '@mui/material'
import { PromoPlanDto, useGetCurrentPromoPlans } from '@src/data/api/merchants-api/merchants-api'
import { CUSTOM_BUYDOWN_FAMILY } from '@src/data/types/Constants'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCustomBuydownDialog } from './CustomBuydownDialog'
import type { MerchantPromotionFormValues } from './tabs/MerchantPromotionTab'

const formatPercent = (value: number) => `${(value * 100).toFixed(2)}%`

interface Props {
  merchantId: string
}

const Fact = ({ label, value }: { label: string; value: string }) => (
  <Grid size={{ xs: 6, sm: 4, md: 2 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography>{value}</Typography>
  </Grid>
)

const BuydownCard = ({ plan, onEdit }: { plan: PromoPlanDto; onEdit: () => void }) => {
  const { t } = useTranslation()
  return (
    <Card variant="outlined">
      <CardHeader
        title={t('merchant.customBuydown.title')}
        slotProps={{ title: { variant: 'subtitle1', color: 'primary' } }}
        action={
          <IconButton aria-label={t('merchant.customBuydown.editTitle')} onClick={onEdit} size="small">
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Stack gap={2.5}>
          <Grid container columns={12} spacing={2}>
            <Fact label={t('merchant.customBuydown.planCode')} value={plan.promoCodePrefix || plan.label} />
            <Fact label={t('merchant.customBuydown.contribution')} value={formatPercent(plan.merchantFeeRate)} />
            <Fact label={t('merchant.customBuydown.adminFee')} value={formatPercent(plan.borrowerFeeRate)} />
            <Fact
              label={t('merchant.customBuydown.maxFinancingAmount')}
              value={plan.maxFinancingAmount ? formatCurrency(plan.maxFinancingAmount) : t('merchant.customBuydown.noMaximum')}
            />
            <Fact label={t('merchant.promo.activeFrom')} value={formatDate(plan.activeFrom)} />
            <Fact
              label={t('merchant.promo.expiresOn')}
              value={plan.expiresOn ? formatDate(plan.expiresOn) : t('merchant.promo.noExpiresOn')}
            />
          </Grid>
          <Box>
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography variant="caption" color="text.secondary">
                {t('merchant.customBuydown.customerRates')}
              </Typography>
              <Help title={t('merchant.customBuydown.customerRatesTooltip')} />
            </Stack>
            <Box
              sx={{ mt: 0.5, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 1.5 }}
            >
              {plan.promoTermRates.map((rate) => (
                <StatCard
                  key={rate.termMonths}
                  variant="outlined"
                  label={`${rate.termMonths} ${t('common.months')}`}
                  value={formatPercent(rate.promoRate)}
                />
              ))}
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

/**
 * The "Custom promotion" half of the promotion tab. A merchant has at most one Custom Buydown:
 * shows the big plus empty-state when none exists, otherwise the buydown card with an edit action.
 * Create/edit both flow through the same dialog. Deletion is intentionally not offered here.
 */
const CustomPromotionPanel = ({ merchantId }: Props) => {
  const { t } = useTranslation()
  const { control, trigger } = useFormContext<MerchantPromotionFormValues>()
  const [promoPlans, isFetching] = useGetCurrentPromoPlans(merchantId)
  const dialog = useCustomBuydownDialog(merchantId)

  const customBuydown = promoPlans.find((plan) => plan.family === CUSTOM_BUYDOWN_FAMILY) ?? null

  if (isFetching) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Card variant="outlined">
        <CardHeader
          title={t('merchant.customBuydown.notificationsTitle')}
          subheader={t('merchant.customBuydown.notificationsDescription')}
          slotProps={{
            title: { variant: 'subtitle1', color: 'primary' },
            subheader: { variant: 'body2' },
          }}
        />
        <CardContent sx={{ pt: 0 }}>
          <ControlledChipsField
            name="promoNotificationEmailList"
            control={control}
            trigger={trigger}
            label={t('merchant.customBuydown.notificationEmails')}
            placeholder={t('merchant.customBuydown.notificationEmailsPlaceholder')}
            helperText={t('merchant.customBuydown.notificationEmailsHelper')}
            required
            format={(v) => v.toLowerCase()}
          />
        </CardContent>
      </Card>

      {customBuydown ? (
        <BuydownCard plan={customBuydown} onEdit={() => void dialog.open(customBuydown)} />
      ) : (
        <AreaButton
          onClick={() => void dialog.open(null)}
          icon={<AddIcon fontSize="large" />}
          title={t('merchant.customBuydown.emptyTitle')}
          description={t('merchant.customBuydown.emptyDescription')}
        />
      )}
    </>
  )
}

export default CustomPromotionPanel
